import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  ListItemButton,
  Menu,
  MenuItem,
  Select
} from '@mui/material';
import { 
  Edit, 
  Delete, 
  Lock, 
  PersonRemove, 
  PersonAdd, 
  SupervisorAccount, 
  SwapHoriz, 
  MoreVert,
  Add,
  Remove,
  Block,
  CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const TeamManagement = () => {
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogData, setDialogData] = useState({ name: '', password: '' });
  const [teamlessUsers, setTeamlessUsers] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [roleAnchorEl, setRoleAnchorEl] = useState(null);
  const [showMasterDialog, setShowMasterDialog] = useState(false);
  const [pendingBidderId, setPendingBidderId] = useState(null);
  const [isEditingExistingBidder, setIsEditingExistingBidder] = useState(false);
  const [selectedMaster, setSelectedMaster] = useState('');
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [selectedTeamForRoles, setSelectedTeamForRoles] = useState(null);
  const [newMemberRole, setNewMemberRole] = useState('member');
  const [newMemberMaster, setNewMemberMaster] = useState('');
  const [roleSelectionDialogOpen, setRoleSelectionDialogOpen] = useState(false);
  const [userToAdd, setUserToAdd] = useState(null);

  useEffect(() => {
    fetchTeams();
    fetchTeamlessUsers();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamMembers(selectedTeam._id);
    } else {
      // When teamless is selected, refresh the teamless users list
      fetchTeamlessUsers();
    }
  }, [selectedTeam]);

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/all`);
      // Filter out any null or undefined values from the response
      const validTeams = response.data.filter(team => team != null);
      const sortedTeams = validTeams.sort((a, b) => a.name.localeCompare(b.name));
      setTeams(sortedTeams);
      if (!selectedTeam && sortedTeams.length > 0) {
        setSelectedTeam(sortedTeams[0]);
      }
    } catch (error) {
      toast.error('Error fetching teams');
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/team-members`, {
        params: { teamId }
      });
      // Filter out any null or undefined values from the response
      const validMembers = response.data.filter(member => member != null);
      setTeamMembers({ ...teamMembers, [teamId]: validMembers });
    } catch (error) {
      toast.error('Error fetching team members');
    }
  };

  const fetchTeamlessUsers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/teamless`);
      // Filter out any null or undefined values from the response
      const validUsers = response.data.filter(user => user != null);
      setTeamlessUsers(validUsers);
    } catch (error) {
      toast.error('Error fetching teamless users');
    }
  };

  const handleDialog = (type, data = null) => {
    setDialogType(type);
    if (type === 'resetPassword') {
      setDialogData({ ...data, password: '' });
    } else {
      setDialogData(data || { name: '', password: '' });
    }
    setDialogOpen(true);
  };

  const handleOpenRoleSelection = (user) => {
    setUserToAdd(user);
    setNewMemberRole('member');
    setNewMemberMaster('');
    setRoleSelectionDialogOpen(true);
  };

  const handleAddMember = async () => {
    if (!userToAdd) return;

    try {
      // Validate master selection if role is bidder
      if (newMemberRole === 'bidder' && !newMemberMaster) {
        toast.error('Please select a master for the bidder');
        return;
      }

      const requestData = {
        teamId: selectedTeam._id,
        userId: userToAdd._id,
        role: newMemberRole
      };

      if (newMemberRole === 'bidder') {
        requestData.masterId = newMemberMaster;
      }

      await axios.post(`${process.env.REACT_APP_API_URL}/teams/add-member`, requestData);
      toast.success('Member added successfully');
      fetchTeamMembers(selectedTeam._id);
      fetchTeamlessUsers();
      setNewMemberRole('member');
      setNewMemberMaster('');
      setRoleSelectionDialogOpen(false);
      setUserToAdd(null);
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add member');
    }
  };

  const handleSubmit = async () => {
    try {
      switch (dialogType) {
        case 'addTeam':
          await axios.post(`${process.env.REACT_APP_API_URL}/teams/add-team`, { 
            name: dialogData.name 
          });
          break;
        case 'editTeam':
          await axios.put(
            `${process.env.REACT_APP_API_URL}/teams/update-team/${dialogData._id}`, 
            { name: dialogData.name }
          );
          break;
      }
      toast.success('Operation completed successfully');
      fetchTeams();
      if (selectedTeam && dialogType === 'editTeam' && selectedTeam._id === dialogData._id) {
        setSelectedTeam({ ...selectedTeam, name: dialogData.name });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
    setDialogOpen(false);
  };

  const handleDeleteTeam = async (teamId) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/teams/delete-team/${teamId}`);
        toast.success('Team deleted successfully');
        
        // Fetch updated teams list
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/teams/all`);
        const sortedTeams = response.data.sort((a, b) => a.name.localeCompare(b.name));
        setTeams(sortedTeams);
        
        // If the deleted team was selected, select another team if available
        if (selectedTeam?._id === teamId) {
          if (sortedTeams.length > 0) {
            setSelectedTeam(sortedTeams[0]);
          } else {
            setSelectedTeam(null);
          }
        }
      } catch (error) {
        toast.error('Error deleting team');
      }
    }
  };

  const handleMoveToTeam = async (user, newTeamId) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/teams/change-team/${user._id}`,
        { newTeamId }
      );
      toast.success('Team changed successfully');
      fetchTeamMembers(selectedTeam._id);
      fetchTeamlessUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to change team');
    }
    setDialogOpen(false);
  };

  const handleSetLead = async (user) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/teams/set-team-lead/${selectedTeam._id}`,
        { userId: user._id }
      );
      toast.success('Team lead set successfully');
      fetchTeamMembers(selectedTeam._id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to set team lead');
    }
  };

  const handleResetPassword = async (user, newPassword) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/teams/reset-password/${user._id}`,
        { newPassword }
      );
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    }
    setDialogOpen(false);
  };

  const handleDeleteUser = async (user) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${user.name}? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/users/delete-user/${user._id}`);
      toast.success('User deleted successfully');
      fetchTeamlessUsers();
      if (selectedTeam) {
        fetchTeamMembers(selectedTeam._id);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleRemoveFromTeam = async (user) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/teams/change-team/${user._id}`,
        { newTeamId: null }
      );
      toast.success('User removed from team successfully');
      fetchTeamMembers(selectedTeam._id);
      fetchTeamlessUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to remove user from team');
    }
  };

  const handleMenuOpen = (event, member) => {
    setAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMember(null);
  };

  const handleChangeRole = async (user, newRole) => {
    try {
      if (newRole === 'bidder') {
        // Show master selection dialog
        setPendingBidderId(user._id);
        setIsEditingExistingBidder(false);
        setShowMasterDialog(true);
        return;
      }
      
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/change-role/${user._id}`,
        { newRole }
      );
      toast.success('Role updated successfully');
      fetchTeamMembers(selectedTeam._id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update role');
    }
  };

  const handleSetMaster = async (bidderId) => {
    setPendingBidderId(bidderId);
    setIsEditingExistingBidder(true);
    setShowMasterDialog(true);
  };

  const handleMakeBidder = async () => {
    if (!selectedMaster) {
      toast.error("Please select a master");
      return;
    }
    
    try {
      if (isEditingExistingBidder) {
        // Update existing bidder's master
        await axios.put(`${process.env.REACT_APP_API_URL}/users/${pendingBidderId}/set-master`, {
          masterId: selectedMaster
        });
        toast.success("Master updated successfully");
      } else {
        // Make new bidder
        await axios.put(`${process.env.REACT_APP_API_URL}/users/change-role/${pendingBidderId}`, {
          newRole: 'bidder',
          masterId: selectedMaster
        });
        toast.success("User made bidder successfully");
      }
      
      setShowMasterDialog(false);
      setSelectedMaster('');
      setPendingBidderId(null);
      setIsEditingExistingBidder(false);
      fetchTeamMembers(selectedTeam._id);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update bidder');
    }
  };

  const handleAddRole = async () => {
    if (!newRole.trim()) {
      toast.error('Please enter a role name');
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/teams/add-team-role/${selectedTeamForRoles._id}`, {
        role: newRole.trim()
      });
      toast.success('Role added successfully');
      setNewRole('');
      setRoleDialogOpen(false);
      fetchTeams(); // Refresh teams to get updated roles
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to add role');
    }
  };

  const handleRemoveRole = async (team, roleToRemove) => {
    if (window.confirm(`Are you sure you want to remove the role "${roleToRemove}" from ${team.name}?`)) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/teams/remove-team-role/${team._id}/${encodeURIComponent(roleToRemove)}`);
        toast.success('Role removed successfully');
        fetchTeams(); // Refresh teams to get updated roles
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to remove role');
      }
    }
  };

  const handleToggleDisabled = async (user) => {
    try {
      const newDisabledStatus = !user.disabled;
      await axios.put(`${process.env.REACT_APP_API_URL}/users/toggle-disabled/${user._id}`, {
        disabled: newDisabledStatus
      });
      toast.success(`User ${newDisabledStatus ? 'disabled' : 'enabled'} successfully`);
      
      // Refresh the lists
      if (selectedTeam) {
        fetchTeamMembers(selectedTeam._id);
      } else {
        fetchTeamlessUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update user status');
    }
    handleMenuClose();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">Team Management</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Paper sx={{ width: '40%', p: 2 }}>
          <Typography variant="h6" gutterBottom>Teams</Typography>
          <List>
            {teams.filter(team => team != null).map((team) => (
              <ListItem
                key={team._id}
                disablePadding
                selected={selectedTeam?._id === team._id}
              >
                <ListItemButton
                  onClick={() => setSelectedTeam(team)}
                  sx={{
                    "&.Mui-selected": {
                      backgroundColor: "action.selected",
                    },
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <ListItemText primary={team.name} />
                    {team.role && team.role.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {team.role.map((role, index) => (
                          <Box
                            key={index}
                            sx={{
                              backgroundColor: 'primary.main',
                              color: 'white',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              fontSize: '0.75rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            {role}
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveRole(team, role);
                              }}
                              sx={{ 
                                color: 'white', 
                                p: 0,
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }
                              }}
                            >
                              <Remove fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTeamForRoles(team);
                        setRoleDialogOpen(true);
                      }}
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      <Add />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDialog('editTeam', team);
                      }}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTeam(team._id);
                      }}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem>
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleDialog('addTeam')}
                startIcon={<PersonAdd />}
              >
                Add Team
              </Button>
            </ListItem>
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding selected={selectedTeam === null}>
              <ListItemButton
                onClick={() => setSelectedTeam(null)}
                sx={{
                  "&.Mui-selected": {
                    backgroundColor: "action.selected",
                  },
                }}
              >
                <ListItemText primary="Teamless" />
              </ListItemButton>
            </ListItem>
          </List>
        </Paper>

        <Paper sx={{ width: '60%', p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {selectedTeam ? `${selectedTeam.name} Members` : 'Teamless Users'}
          </Typography>
          
          {selectedTeam ? (
            <List>
              {/* Display Team Lead and Members */}
              {teamMembers[selectedTeam._id]
                ?.filter(member => member != null && member.role !== 'bidder')
                .map((member) => (
                <ListItem key={member._id}>
                  <ListItemText 
                    primary={
                      <>
                        {member.name}
                        {member.disabled && (
                          <Typography component="span" sx={{ ml: 1, color: 'error.main', fontSize: '0.875rem' }}>
                            (Disabled)
                          </Typography>
                        )}
                      </>
                    }
                    secondary={
                      <>
                        {member.email}
                        <br />
                        Role: {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                      </>
                    } 
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      onClick={(e) => handleMenuOpen(e, member)}
                    >
                      <MoreVert />
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}

              {/* Divider and Bidders Section */}
              {teamMembers[selectedTeam._id]?.some(member => member != null && member.role === 'bidder') && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ ml: 2, mb: 1 }}>Bidders</Typography>
                  {teamMembers[selectedTeam._id]
                    ?.filter(member => member != null && member.role === 'bidder')
                    .map((member) => {
                      const master = teamMembers[selectedTeam._id]?.find(m => m._id === member.master);
                      return (
                        <ListItem key={member._id}>
                          <ListItemText 
                            primary={
                              <>
                                {member.name}
                                {member.disabled && (
                                  <Typography component="span" sx={{ ml: 1, color: 'error.main', fontSize: '0.875rem' }}>
                                    (Disabled)
                                  </Typography>
                                )}
                              </>
                            }
                            secondary={
                              <>
                                {member.email}
                                <br />
                                Role: {member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                {master && <br />}
                                {master && `Master: ${master.name}`}
                                {!master && <br />}
                                {!master && 'No master assigned'}
                              </>
                            } 
                          />
                          <ListItemSecondaryAction>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleSetMaster(member._id)}
                              sx={{ mr: 1 }}
                            >
                              Set Master
                            </Button>
                            <Button
                              size="small"
                              onClick={(e) => handleMenuOpen(e, member)}
                            >
                              <MoreVert />
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                </>
              )}

              <ListItem>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleDialog('addMember')}
                  startIcon={<PersonAdd />}
                >
                  Add Member
                </Button>
              </ListItem>
            </List>
          ) : (
            <List>
              {teamlessUsers.filter(user => user != null).map((user) => (
                <ListItem key={user._id}>
                  <ListItemText 
                    primary={
                      <>
                        {user.name}
                        {user.disabled && (
                          <Typography component="span" sx={{ ml: 1, color: 'error.main', fontSize: '0.875rem' }}>
                            (Disabled)
                          </Typography>
                        )}
                      </>
                    }
                    secondary={user.email}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleDeleteUser(user)}
                      color="error"
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <Delete />
                    </IconButton>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, user)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedMember && (
          <>
            <MenuItem 
              onClick={(event) => {
                event.stopPropagation();
                setRoleAnchorEl(event.currentTarget);
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SupervisorAccount />
                <Typography>Change Role</Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                handleDialog('changeTeam', { user: selectedMember });
                handleMenuClose();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SwapHoriz />
                <Typography>Change Team</Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                handleDialog('resetPassword', selectedMember);
                handleMenuClose();
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Lock />
                <Typography>Reset Password</Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                handleToggleDisabled(selectedMember);
              }}
              sx={{ color: selectedMember?.disabled ? 'success.main' : 'warning.main' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedMember?.disabled ? <CheckCircle /> : <Block />}
                <Typography>{selectedMember?.disabled ? 'Enable User' : 'Disable User'}</Typography>
              </Box>
            </MenuItem>
            <MenuItem 
              onClick={() => {
                handleDeleteUser(selectedMember);
                handleMenuClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Delete />
                <Typography>Delete User</Typography>
              </Box>
            </MenuItem>
          </>
        )}
      </Menu>

      <Menu
        anchorEl={roleAnchorEl}
        open={Boolean(roleAnchorEl)}
        onClose={() => setRoleAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem 
          onClick={() => {
            handleChangeRole(selectedMember, 'lead');
            setRoleAnchorEl(null);
            handleMenuClose();
          }}
        >
          Team Lead
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleChangeRole(selectedMember, 'member');
            setRoleAnchorEl(null);
            handleMenuClose();
          }}
        >
          Member
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleChangeRole(selectedMember, 'restricted developer');
            setRoleAnchorEl(null);
            handleMenuClose();
          }}
        >
          Restricted Developer
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleChangeRole(selectedMember, 'bidder');
            setRoleAnchorEl(null);
            handleMenuClose();
          }}
        >
          Bidder
        </MenuItem>
      </Menu>

      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)}
        maxWidth={dialogType === 'addMember' ? 'sm' : 'xs'}
        fullWidth
      >
        <DialogTitle>
          {dialogType === 'addTeam' ? 'Add Team' :
           dialogType === 'editTeam' ? 'Edit Team' :
           dialogType === 'addMember' ? 'Add Team Member' :
           dialogType === 'changeTeam' ? 'Change Team' :
           'Reset Password'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'addMember' ? (
            <List>
              {teamlessUsers.filter(user => user != null).length > 0 ? (
                teamlessUsers.filter(user => user != null).map((user) => (
                  <ListItem key={user._id}>
                    <ListItemText 
                      primary={user.name}
                      secondary={user.email}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleOpenRoleSelection(user)}
                    >
                      Add to Team
                    </Button>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No users available to add" />
                </ListItem>
              )}
            </List>
          ) : dialogType === 'changeTeam' ? (
            <List>
              <ListItem>
                <ListItemText
                  primary={`Change team for ${dialogData.user.name}`}
                  secondary="Select a new team or remove from current team"
                />
              </ListItem>
              <ListItem>
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={() => handleMoveToTeam(dialogData.user, null)}
                >
                  Remove from Current Team
                </Button>
              </ListItem>
              <Divider sx={{ my: 2 }} />
              {teams
                .filter(team => team && team?._id !== selectedTeam?._id)
                .map((team) => (
                  <ListItem key={team._id}>
                    <ListItemText primary={team.name} />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleMoveToTeam(dialogData.user, team._id)}
                    >
                      Move to Team
                    </Button>
                  </ListItem>
              ))}
            </List>
          ) : dialogType === 'resetPassword' ? (
            <>
              <TextField
                type="password"
                margin="dense"
                label="New Password"
                fullWidth
                value={dialogData.password}
                onChange={(e) => setDialogData({ ...dialogData, password: e.target.value })}
              />
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleResetPassword(dialogData, dialogData.password)}
                >
                  Reset Password
                </Button>
              </Box>
            </>
          ) : (
            <TextField
              autoFocus
              margin="dense"
              label="Team Name"
              fullWidth
              value={dialogData.name}
              onChange={(e) => setDialogData({ ...dialogData, name: e.target.value })}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Close
          </Button>
          {(dialogType === 'addTeam' || dialogType === 'editTeam') && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogType === 'addTeam' ? 'Add' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Master Selection Dialog */}
      <Dialog open={showMasterDialog} onClose={() => setShowMasterDialog(false)}>
        <DialogTitle>
          {isEditingExistingBidder ? 'Set Master for Bidder' : 'Select Master for Bidder'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isEditingExistingBidder 
              ? 'Select a team member who will be the master for this bidder. The bidder will use the master\'s resumes.'
              : 'Select a team member who will be the master for this bidder. The bidder will use the master\'s resumes.'
            }
          </Typography>
          <Select
            fullWidth
            value={selectedMaster}
            onChange={(e) => setSelectedMaster(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select master
            </MenuItem>
            {teamMembers[selectedTeam?._id]
              ?.filter(member => member != null && member.role !== 'bidder')
              .map((member) => (
                <MenuItem key={member._id} value={member._id}>
                  {member.name} ({member.role})
                </MenuItem>
              ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMasterDialog(false)}>Cancel</Button>
          <Button onClick={handleMakeBidder} variant="contained">
            {isEditingExistingBidder ? 'Update Master' : 'Make Bidder'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Role Dialog */}
      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
        <DialogTitle>
          Add Role to {selectedTeamForRoles?.name}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
            placeholder="e.g., blockchain, special, frontend"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddRole} variant="contained">
            Add Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role Selection Dialog */}
      <Dialog 
        open={roleSelectionDialogOpen} 
        onClose={() => setRoleSelectionDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Select Role for {userToAdd?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Role
            </Typography>
            <Select
              fullWidth
              value={newMemberRole}
              onChange={(e) => {
                setNewMemberRole(e.target.value);
                if (e.target.value !== 'bidder') {
                  setNewMemberMaster('');
                }
              }}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="restricted developer">Restricted Developer</MenuItem>
              <MenuItem value="bidder">Bidder</MenuItem>
            </Select>
          </Box>

          {newMemberRole === 'bidder' && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Master
              </Typography>
              <Select
                fullWidth
                value={newMemberMaster}
                onChange={(e) => setNewMemberMaster(e.target.value)}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  Select a master
                </MenuItem>
                {teamMembers[selectedTeam?._id]
                  ?.filter(member => member != null && member.role !== 'bidder')
                  .map((member) => (
                    <MenuItem key={member._id} value={member._id}>
                      {member.name} ({member.role})
                    </MenuItem>
                  ))}
              </Select>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleSelectionDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAddMember} variant="contained">
            Add to Team
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TeamManagement;
